import { X } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import { GOVT_SCHEMES } from "@vaulzo/core/domain/govt";

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for government schemes: one row per scheme with the
 * amount invested and the current balance (both from your statements —
 * these have no market feed). Gain badges compare the two.
 */
export default function GovtHoldingsEditor({ holdings, setHoldings }) {
  const patchRow = (i, patch) =>
    setHoldings((rows) => rows.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings((rows) => rows.filter((_, idx) => idx !== i));
  const addRow = () =>
    setHoldings((rows) => [...rows, { scheme: "NPS", label: "", invested: "", current: "" }]);

  let totalInvested = 0;
  let totalCurrent = 0;
  let gainInvested = 0;
  let gainCurrent = 0;
  holdings.forEach((h) => {
    const cur = parseFloat(h.current);
    const inv = parseFloat(h.invested);
    if (cur > 0) totalCurrent += cur;
    if (inv > 0) totalInvested += inv;
    if (cur > 0 && inv > 0) {
      gainInvested += inv;
      gainCurrent += cur;
    }
  });
  const gain = gainInvested > 0 ? ((gainCurrent - gainInvested) / gainInvested) * 100 : null;

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Schemes</span>
        <span className="text-[12px] text-vaulzo-fainter">From your statements — no live feed</span>
      </div>

      {holdings.map((h, i) => {
        const cur = parseFloat(h.current);
        const inv = parseFloat(h.invested);
        const rowGain = cur > 0 && inv > 0 ? ((cur - inv) / inv) * 100 : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-3 first:pt-1 last:border-b-0">
            <div className="flex items-center gap-4">
              <label className="w-[90px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Scheme
                </span>
                <select
                  value={h.scheme}
                  onChange={(e) => patchRow(i, { scheme: e.target.value })}
                  className={`w-full cursor-pointer text-[15px] font-semibold ${underline}`}
                >
                  {GOVT_SCHEMES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              {h.scheme === "OTHER" && (
                <label className="min-w-0 flex-1">
                  <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                    Name
                  </span>
                  <input
                    value={h.label}
                    onChange={(e) => patchRow(i, { label: e.target.value })}
                    placeholder="e.g. NSC"
                    className={`w-full text-[15px] ${underline}`}
                  />
                </label>
              )}
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove scheme"
                className="flex w-5 flex-none justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            <div className="mt-2 flex gap-4">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Invested (₹)
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="numeric"
                  value={h.invested}
                  onChange={(e) => patchRow(i, { invested: e.target.value })}
                  placeholder="Optional — shows your gain"
                  className={`num w-full text-[15px] ${underline}`}
                />
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Current (₹)
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="numeric"
                  required
                  value={h.current}
                  onChange={(e) => patchRow(i, { current: e.target.value })}
                  placeholder="85704"
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
            </div>

            {rowGain != null && (
              <div className="num pt-2 text-[12px] text-vaulzo-fainter">
                {formatCurrency(inv)} → {formatCurrency(cur)}
                <span className={rowGain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                  {" "}
                  · {rowGain >= 0 ? "↑" : "↓"} {Math.abs(rowGain).toFixed(2)}%
                </span>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add scheme
      </button>

      {totalCurrent > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total <span className="font-semibold text-vaulzo-ink">{formatCurrency(totalCurrent)}</span>
          {totalInvested > 0 && <> · invested {formatCurrency(totalInvested)}</>}
          {gain != null && (
            <span className={`font-semibold ${gain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}`}>
              {" "}
              · {gain >= 0 ? "↑" : "↓"} {Math.abs(gain).toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
